import { useState } from "react";
import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import ActionButton from "@/components/base/ActionButton";
import { faqs } from "@/mocks/faqs";

export default function Faq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <>
      <Seo
        title="FAQ | EV Retrofit Questions Answered"
        description="Answers to common EV retrofit questions — which vehicles can be converted, battery and range, cost, legality, certification, commercial vehicles and maintenance."
        keywords="EV retrofit FAQ, EV conversion questions, ICE to EV conversion cost, retrofit certification"
        path="/faq"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        }}
      />
      <PageHero
        eyebrow="FAQ"
        breadcrumb="FAQ"
        title="EV Retrofit — Questions Answered Responsibly"
        description="Retrofit depends on the vehicle. These answers explain how the process works and why several things can only be confirmed after a technical assessment."
      >
        <ActionButton to="/ev-retrofit#eligibility" variant="primary" size="md" icon="ri-flashlight-line">
          Check Retrofit Eligibility
        </ActionButton>
      </PageHero>

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
                  Responsible Answers, Not Promises
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  We do not publish a universal cost, range, battery size or certification. Those depend
                  on the vehicle, the retrofit design and applicable regulations.
                </p>
                <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
                  <p className="font-label text-xs font-semibold uppercase tracking-[0.14em] text-foreground-500">
                    Still have a question?
                  </p>
                  <p className="mt-2 text-sm text-foreground-600">
                    Send your question and the team will respond through the official channel.
                  </p>
                  <div className="mt-4">
                    <ActionButton to="/contact" variant="dark" size="sm" icon="ri-mail-send-line">
                      Ask a Question
                    </ActionButton>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="lg:col-span-8">
              <div className="flex flex-col gap-3">
                {faqs.map((item, index) => {
                  const isOpen = open === index;
                  return (
                    <Reveal key={item.question} delay={index * 0.03}>
                      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : index)}
                          aria-expanded={isOpen}
                          className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left md:px-6 md:py-5"
                        >
                          <span className="text-sm font-semibold text-foreground-950 md:text-base">
                            {item.question}
                          </span>
                          <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors ${
                              isOpen ? "bg-primary-500 text-foreground-950" : "bg-background-50 text-foreground-700"
                            }`}
                          >
                            <i className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} text-lg`} />
                          </span>
                        </button>
                        {isOpen ? (
                          <div className="border-t border-background-200 px-5 py-4 md:px-6 md:py-5">
                            <p className="text-sm leading-relaxed text-foreground-600">{item.answer}</p>
                          </div>
                        ) : null}
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}